import React, { useEffect, useState } from 'react';
import { hookstate, useHookstate } from '@hookstate/core';
import { usePathname } from 'next/navigation';

import { chargeFormat, currencyFormat, imgFormat } from '~/helpers/formatter';
import prodHelper from '~/helpers/product';
import { cuotaState } from '~/store/States/checkout';
import { CDN, CURRENCY, CURRENCY_SYMBOL, LOCALE, PLAN, PLANES_CLARO_HOGAR, TYPE_PLAN } from '~/store/States/global';
import { cartPospago, usePagoUnico, usePospagoState } from '~/store/States/pospago';
import useStores from '~/store/StoreHook';

import { Link } from '#/renderer/router';
import InfoFijo from '~/containers/Cart/InfoFijo';
import { useEspotData } from '~/store/Query/api';

export const esimConfig = hookstate({});

const CartSummaryImg = ({ itemInfo = {} }) => (
  <picture>
    <img src={itemInfo?.images?.length ? imgFormat('90x90', `${CDN}${itemInfo.images[0]}`) : ''} alt="alt" />
  </picture>
);

const CartSummaryDesc = ({ itemInfo = {}, addonsList, quantity, hasEsim = false, isIFI, kitItem }) => {
  const esimConfigLocal = useHookstate(esimConfig);
  const clarohogar = useEspotData(PLANES_CLARO_HOGAR, isIFI);
  const attrMap = prodHelper.convertAttributes(itemInfo.attributes);
  let specs;
  let isAddOn = false;
  let priceText;
  let priceTextSplit;
  let kitItemPrice;
  if (Object.keys(itemInfo).length) {
    specs = (itemInfo.attributes).reduce((a, b, i) => {
      const index = `cartSummary-spec-${i}`;
      if (b.identifier === 'def-capacidad' || b.identifier === 'def-color' || b.identifier === 'def-pantalla') {
        a.push(<p key={index}><span>{b.value}</span></p>);
      }
      if (b.identifier === 'attr-conf-tipoproducto' && b.value === 'ADDONS') {
        isAddOn = true;
      }
      if (isAddOn && b.identifier === 'attr-short-description') {
        a.push(<p key={index}><span>{b.value}</span></p>);
      }
      if (isIFI && b.identifier === 'attr-conf-precio-mensual') {
        priceText = currencyFormat(b.value, 1);
        priceTextSplit = priceText.split('.');
        const { plan, plazo, pricing } = cartPospago(kitItem?.part_number_kit);
        kitItemPrice = currencyFormat(kitItem?.product?.items[0]?.pricing?.[pricing]?.[plan]?.[plazo]?.offer_price || 0, 1).split('.');
      }
      return a;
    }, []);
  }

  const ifiLinea = isIFI && attrMap['attr-inalambrico-linea']?.toUpperCase() === 'SI';

  const esimConfigToUse = esimConfigLocal.get();
  const conEsimTexto = esimConfigToUse?.cart?.conEsim ?? '';

  if (hasEsim) {
    specs.push(<p key="esim"><span>{conEsimTexto}</span></p>)
  }

  const fullname = Object.keys(itemInfo).length ? itemInfo.locales[LOCALE].name.replace(/<br>/, '\n').replace(/<br\/>/, '\n').replace(/\\n/, '\n').split('\n')
    .map((str, index) => <p key={`title--${itemInfo.part_number}-${index}`}>{str}</p>) : '';
  return (
    <div className="cart--summary-desc">
      <b>{addonsList.some(x => x.item_part_number === itemInfo.part_number) ? 'Adicional' : 'Producto'}</b>
        {isIFI ? (
          <>
            <div className="ifi">
              <InfoFijo fullname={fullname} priceText={priceText} />
              <p className="price">
                <span className="currency-symbol">{CURRENCY_SYMBOL}</span>
                <span className="value">{priceTextSplit[0]}.</span>
                <span className="suffix">{priceTextSplit[1]}/mes</span>
              </p>
            </div>
            <div className="ifi">
              <div>
                <p>{kitItem.product.items[0].locales[LOCALE].name}</p>
                {ifiLinea && (
                  <p>{clarohogar?.linea.prefix}{clarohogar?.linea.nombre}</p>
                )}
              </div>
              <p className="price">
                <span className="currency-symbol">{CURRENCY_SYMBOL}</span>
                <span className="value">{kitItemPrice[0]}.</span>
                <span className="suffix">{kitItemPrice[1]}</span>
              </p>
            </div>
          </>
        ) : (
          <div>
            {fullname} {quantity > 1 && `(${quantity})`}
          </div>
        )}
      {specs}
    </div>
  );
};

const CartSummarySub = ({ itemInfo = {}, quantity, cartItem, cart }) => {
  const pagoUnico = usePagoUnico();
  const attrMap = prodHelper.convertAttributes(itemInfo.attributes);
  const tipo = attrMap['attr-conf-tipoproducto'];
  const tipoPlan = attrMap['attr-conf-tipoplan']?.toUpperCase() || '';
  const precioMensual = attrMap['attr-conf-precio-mensual'];
  const noIncluido = attrMap['attr-conf-todo-incluido']?.toUpperCase() === 'NO';
  let currencyText;
  let isPagoUnico = false;
  let plazo;
  if (cartItem.part_number_kit) {
    const { plan, plazo: plz, pricing, isPagoUnico: iPU } = cartPospago(cartItem.part_number_kit);
    isPagoUnico = iPU;
    plazo = plz;
    let price = itemInfo.pricing[pricing][plan][plazo].offer_price;
    if (isPagoUnico) {
      const itemPlan = cart.find(it => it.product.type === TYPE_PLAN)?.product.items[0] ?? {};
      const attrPlan = prodHelper.convertAttributes(itemPlan.attributes);
      const planPrice = +attrPlan['attr-conf-precio-mensual'];
      const totalPlan = planPrice * plazo;
      price -= totalPlan;
      price /= plazo;
    }
    currencyText = currencyFormat(price, 1).split('.');
  } else if (tipo === PLAN) {
    let price = +precioMensual;
    const kitItem = cart.find(it => it.part_number_kit);
    if (kitItem) {
      const { plan, plazo: plz, pricing, isPagoUnico: iPU } = cartPospago(kitItem.part_number_kit);
      isPagoUnico = iPU;
      plazo = plz;
      if (noIncluido) {
        price += (kitItem.product.items[0].pricing[pricing][plan][plazo].financing || 0) / plazo;
      }
      currencyText = currencyFormat(price, 1).split('.');
    }
  } else {
    currencyText = currencyFormat(itemInfo.pricing[CURRENCY].offer_price * quantity).split('.');
    if (currencyText[0] === '0' && currencyText[1] === '00' && precioMensual) {
      currencyText = currencyFormat(precioMensual, 1).split('.');
    }
  }

  return (
    <div className="cart--summary-sub">
      <b>{isPagoUnico ? pagoUnico?.resumen.precio.replace(/{NUMERO_CUOTAS}/g, plazo) : 'Precio'}</b>
      <p>
        <span className="symbol">{CURRENCY_SYMBOL}</span>
        <span>{currencyText[0]}.</span>
        <span className="symbol">{currencyText[1]}</span>
      </p>
    </div>
  );
};

const CartSummaryDetail = ({ itemInfo = {}, addonsList, quantity, cartItem, cart, hasEsim }) => {
  const pospagoState = usePospagoState(!!cartItem.part_number_kit);
  const isIFI = cartPospago(cartItem.part_number_kit).isIFI;
  if (
    pospagoState.config.product_sku_sin_equipo.value === cartItem.part_number ||
    isIFI
  ) {
    return null;
  }

  const sinEquipoItem = cart.find(x => x.part_number === 'K01_0001_001');
  const kitItem = cart.find(x => x.part_number_kit);
  const hasIFI = cartPospago(kitItem?.part_number_kit).isIFI;

  return (
    <div className="cart--summary-detail">
      <CartSummaryImg itemInfo={itemInfo} />
      <section>
        <CartSummaryDesc
          itemInfo={itemInfo}
          addonsList={addonsList}
          quantity={quantity}
          hasEsim={hasEsim || sinEquipoItem?.esim}
          kitItem={kitItem}
          isIFI={hasIFI}
        />
        {!hasIFI && (
          <CartSummarySub
            itemInfo={itemInfo}
            addonsList={addonsList}
            quantity={quantity}
            cartItem={cartItem}
            cart={cart}
          />
        )}
      </section>
    </div>
  );
};

const UI = ({ cartInfo = {}, addonsList }) => {
  const pathname = usePathname();
  const [chargeTitle, setChargeTitle] = useState();
  const { orderStore } = useStores();
  const [precio_minimo_incluido, setPrecio_minimo_incluido] = useState('');
  const cuotas = useHookstate(cuotaState.cuotas);
  const pagoUnico = usePagoUnico();

  useEffect(() => {
    async function init() {
      if (!orderStore.getCartConfig || !orderStore.getCartConfig.success) {
        await orderStore.getConfig();
      }
      await orderStore.getConfigEsim();
      if (orderStore.getCartConfig.success) {
        setChargeTitle(orderStore.getCartConfig.data.data.chargeTitle);
        if (orderStore.getCartConfig.data.data.precio_minimo_incluido) {
          setPrecio_minimo_incluido(parseFloat(orderStore.getCartConfig.data.data.precio_minimo_incluido));
        }
      }
      if (orderStore.getEsimConfig.success) {
        const esimConfigToSet = orderStore.getEsimConfig.data.data;
        esimConfig.set(esimConfigToSet)
      }
    }
    init();
  }, []);

  const itemInfo = {};
  let isPagoUnico = false;
  let plazo;
  let hasIFI = false;
  const cartDetails = (cartInfo.items || []).map((data, i) => {
    const index = `item--${data.part_number}--${i}`;
    for (const item of data.product.items) {
      itemInfo[item.part_number] = item;
    }
    if (data.part_number_kit) {
      const { plazo: plz, isPagoUnico: iPU, isIFI } = cartPospago(data.part_number_kit);
      isPagoUnico = iPU;
      plazo = plz;
      hasIFI = isIFI;
    }
    return (
      <CartSummaryDetail
        hasEsim={data?.esim}
        key={index}
        itemInfo={itemInfo[data.part_number]}
        addonsList={addonsList}
        quantity={+data.quantity}
        cartItem={data}
        cart={cartInfo.items}
      />
    );
  });

  const clarohogar = useEspotData(PLANES_CLARO_HOGAR, hasIFI);

  let total;
  let total_text;
  let grandTotal;
  if (cartInfo?.amount?.[CURRENCY]) {
    grandTotal = cartInfo.amount[CURRENCY].grand_total;
    const isPayment = pathname === '/checkout/payment';
    if (isPagoUnico) {
      grandTotal /= plazo;
    } else if (isPayment && cuotas.value > 0) {
      grandTotal /= cuotas.value;
    }
    total = grandTotal * 100;
    total_text = currencyFormat(grandTotal).split('.');
  }

  let note;
  if (isPagoUnico) {
    const text = pagoUnico?.resumen.nota && chargeFormat(pagoUnico?.resumen.nota, total);
    note = <p className="cart--summary-note" dangerouslySetInnerHTML={{ __html: text }} />;
  } else if (precio_minimo_incluido){
    if (total <= precio_minimo_incluido) {
      note = chargeFormat(chargeTitle, total);
    }
  }
  return (
    <section className="cart--summary">
      <header className="cart--summary-header">
        <p>Resumen de compra</p>
        <Link to="/cart" id="editar-carrito">
          Editar
        </Link>
      </header>
      {cartDetails}
      <aside className={`cart--summary-total ${hasIFI ? 'ifi-total' : ''}`}>
        <b>
          {isPagoUnico
            ? pagoUnico?.resumen.total
            : hasIFI
            ? clarohogar?.resumen.total
            : cuotas.value
            ? `Tu compra en ${cuotas.value} cuotas:`
            : 'Total:'}
        </b>
        <p>
          <span className="symbol">{CURRENCY_SYMBOL}</span>
          <span>{total_text?.[0]}.</span>
          <span className="symbol">{total_text?.[1]}</span>
          {cuotas.value > 0 && <span>/mes</span>}
        </p>
      </aside>
      {note}
    </section>
  );
};

export default UI;
